// =============================================================================
// src/views/comparador/index.ts — AppModule + SectionController da Tela 03.
//
// Proveniencia: Sections.comparador (assets/unified-app.js 1140-1278) + marcacao
//   #section-comparador (unified.html 573-666). Esta view cumpre DOIS contratos:
//     - AppModule        -> render() (marcacao) + init(ctx) (listeners pos-montagem).
//     - SectionController -> activate(map) / deactivate() (comportamento por scroll-spy).
//   O SectionManager TS (core) chama activate/deactivate ao entrar/sair da secao.
//
// Comportamento portado FIEL: modo inicial 'diff'; tabs .u-comp-mode-tab trocam o
//   modo e reaplicam estilos + insight; cards .u-group-card alternam .disabled e
//   recalculam dominancia/donuts. Bind idempotente via bindOnce (sobrevive a
//   reativacoes do scroll-spy sem duplicar handlers), igual ao original.
//
// SRP: este arquivo so faz o glue DOM<->modos. Markup em comparador-markup.ts;
//   regra dos modos em comparador-modes.ts; donuts em comparador-donut.ts.
// =============================================================================

import type {
  AppContext,
  AppModule,
  MapController,
  SectionController,
  GroupKey,
} from '../../app/types';
import { setActiveItem, bindOnce } from '../../core/ui';
import { qsa } from '../../app/dom';
import { renderComparadorSection } from './comparador-markup';
import {
  createComparadorModeState,
  applyDiffMode,
  applyComparadorMode,
  updateComparadorInsight,
  deactivateComparador,
  type ComparadorMode,
  type ComparadorModeState,
} from './comparador-modes';

/** Estado vivo dos modos (currentMode/disabledGroups/donutMarkers) — escopo de modulo. */
const comparadorState: ComparadorModeState = createComparadorModeState();

/** Liga os 3 tabs de modo: marca o ativo, troca o modo, reaplica estilos + insight. */
function bindComparadorModeTabs(mapCtrl: MapController): void {
  const tabs = qsa<HTMLElement>('.u-comp-mode-tab');
  tabs.forEach((tab) => {
    bindOnce(tab, 'click', 'comparador-mode', () => {
      setActiveItem(tabs, tab);
      comparadorState.currentMode = (tab.dataset.mode ?? 'diff') as ComparadorMode;
      applyComparadorMode(comparadorState, mapCtrl);
      updateComparadorInsight(comparadorState);
    });
  });
}

/** Alterna .disabled de um card, registra no estado e recalcula modo + insight. */
function toggleComparadorGroupCard(card: HTMLElement, mapCtrl: MapController): void {
  const group = (card.dataset.group ?? 'A') as GroupKey;
  card.classList.toggle('disabled');
  comparadorState.disabledGroups[group] = card.classList.contains('disabled');
  applyComparadorMode(comparadorState, mapCtrl);
  updateComparadorInsight(comparadorState);
}

/** Liga os cards de grupo A/B/C (toggle .disabled), idempotente via bindOnce. */
function bindComparadorGroupCards(mapCtrl: MapController): void {
  qsa<HTMLElement>('#section-comparador .u-group-card').forEach((card) => {
    bindOnce(card, 'click', 'comparador-card', () => toggleComparadorGroupCard(card, mapCtrl));
  });
}

/** MapController da ultima activate, consumido por deactivate (FIEL ao singleton original). */
let lastComparadorMapCtrl: MapController | null = null;

/**
 * Entrada na secao (replica Sections.comparador.activate 1146-1170): aplica o modo
 * diff inicial, atualiza o insight e liga tabs + cards. Idempotente via bindOnce.
 * Registra o MapController para que deactivate opere sobre o mesmo controlador.
 */
function activateComparador(mapCtrl: MapController): void {
  lastComparadorMapCtrl = mapCtrl;
  applyDiffMode(comparadorState, mapCtrl);
  updateComparadorInsight(comparadorState);
  bindComparadorModeTabs(mapCtrl);
  bindComparadorGroupCards(mapCtrl);
}

/**
 * AppModule da Tela 03. render() emite a marcacao FIEL; init(ctx) liga o
 * comportamento quando o mapa ja existe (o mapa monta apos fetch assincrono do
 * GeoJSON — guard para ctx.map === null preserva o cenario "mapa ainda nao pronto").
 */
export const comparadorView: AppModule & {
  activate(ctx: AppContext): void;
  deactivate(): void;
} = {
  id: 'comparador',

  render(): string {
    return renderComparadorSection();
  },

  // Ativacao dirigida SO pelo SectionManager (scroll-spy), igual ao unified.html,
  // que ativa apenas mapa-eleitoral no boot. Ativar aqui fazia o comparador mexer
  // no mapa compartilhado fora de vista, sobrepondo o mapa-eleitoral. Fix 2026-05-31.
  init(): void {},

  /**
   * Hook de ciclo de vida do SectionManager (scroll-spy). Re-ativa o modo corrente
   * ao entrar na secao; sem mapa, no-op (a integracao reativa quando o mapa montar).
   */
  activate(ctx: AppContext): void {
    if (!ctx.map) return;
    activateComparador(ctx.map);
  },

  /** Saida da secao (replica deactivate 1274-1277): remove donuts e zera o style fn. */
  deactivate(): void {
    if (lastComparadorMapCtrl) deactivateComparador(comparadorState, lastComparadorMapCtrl);
  },
};

/**
 * Controlador exposto para a integracao acoplar o scroll-spy (activate/deactivate).
 * Registra o MapController na entrada para que deactivate opere sobre o mesmo
 * controlador, espelhando Sections.comparador.activate/deactivate.
 */
export const comparadorSection: SectionController = {
  activate(mapCtrl: MapController): void {
    lastComparadorMapCtrl = mapCtrl;
    activateComparador(mapCtrl);
  },
  deactivate(): void {
    if (lastComparadorMapCtrl) deactivateComparador(comparadorState, lastComparadorMapCtrl);
  },
};

export default comparadorView;
